import { z } from "zod";

import { getApiKey } from "../addin-auth";
import { fetchAllCustomProperties, getDocumentFileToBase64 } from "../document";
import { getEnv } from "../envManager";
import { reportError } from "../sentry-reporter";
import { findEnvironmentByAppUrl, type BackendEnvironment } from "../environments";
import { safeFetch } from "../safe-fetch";
import {
    parseAgreementIdentityCandidates,
    readAgreementIdentityPartXml,
    type AgreementIdentity,
} from "./agreement-identity";
import { wlog } from "./log";
import {
    WOPI_PROPS,
    WOPI_MESSAGE,
    ReadUrlMessageSchema,
    ReadTokenMessageSchema,
    ActionValidatorsSchema,
    ContentControlDifferencesSchema,
    IciProxyResponseSchema,
    IciVisibleActionsSchema,
    VersionLabelsConfigSchema,
    VersionLabelsSchema,
    AgreementSchema,
    TeamMembersSchema,
    CurrentUserSchema,
    WopiPanelDetailsSchema,
    IsLastApproverSchema,
    ConfigurationsSchema,
    ReasonCodesSchema,
    type ActionValidator,
    type ContentControlDifference,
    type IciProxyResponse,
    type IciVisibleAction,
    type WopiToken,
    type JsonValue,
    type TeamMember,
    type CurrentUser,
    type WopiPanelDetails,
    type ReasonCode,
} from "./contract";

const POST_MESSAGE_TIMEOUT_MS = 3_000;
const POLL_INITIAL_INTERVAL_MS = 5_000;
const POLL_MAX_INTERVAL_MS = 60_000;
const DEFAULT_NOTES_CHARACTER_LIMIT = 1000;
const NOTES_CHARACTER_LIMIT_CONFIG_KEY = "Core.Notes.CharacterLimit";
const VERSION_LABELS_CONFIG_KEY = "EnableDocumentVersionLabels";
const RESUME_WORKFLOW_MAX_RETRIES = 3;
const RESUME_RETRY_DELAY_MS = 1_000;
const CONFLICT_STATUS = 409;
const SAVE_FILE_EXTENSION = "Docx";
const NULL_GUID = "00000000-0000-0000-0000-000000000000";
// The proxy answers its own 502 only after the full server-side ICI call has been
// exhausted, so retrying the proxy would re-run that timeout; 502 is dropped from
// the transient set for the proxy hop.
const PROXY_RETRY_STATUSES = [408, 429, 503, 504];
const REJECTED_BEFORE_PROCESSING_STATUSES = [408, 429];
const MUTATING_ICI_ACTIONS = new Set([
    "resumeWorkflow",
    "recallDocument",
    "saveWithDocument",
    "updateWopiSessionState",
    "addTeamMemberWithAssociations",
]);

export function proxyRetryConfig(action: string): { retryStatuses: number[]; retryOnNetworkError: boolean } {
    const mutating = MUTATING_ICI_ACTIONS.has(action);
    return {
        retryStatuses: mutating ? REJECTED_BEFORE_PROCESSING_STATUSES : PROXY_RETRY_STATUSES,
        retryOnNetworkError: !mutating,
    };
}

// Raised when the proxy relays an ICI-side failure — the proxy always answers HTTP
// 200 and carries the ICI status in the envelope with ok=false. Callers branch on
// `status` (the last-approver 409 retry) or surface the message.
export class IciProxyError extends Error {
    constructor(
        readonly action: string,
        readonly status: number,
        readonly body: string,
    ) {
        super(`[wopi] ${action} failed: HTTP ${status}`);
        this.name = "IciProxyError";
    }
}

// ResumeWorkFlowMobile answers 200 even when the workflow rejects the action, with the
// validation messages in the body (a List<ActionValidator>); a non-empty list means
// the action did not complete and must be surfaced instead of reported as success.
function assertResumeAccepted(body: string): void {
    const validators = collectResumeValidators(body);
    if (validators.length > 0) {
        throw new Error(`[wopi] resumeWorkflow rejected: ${validators.join("; ")}`);
    }
}

export function collectResumeValidators(body: string): string[] {
    const trimmed = body.trim();
    if (!trimmed) return [];
    let parsed: unknown;
    try {
        parsed = JSON.parse(trimmed);
    } catch {
        return [];
    }
    if (!Array.isArray(parsed)) return [];
    return parsed.map(validatorMessage).filter((message): message is string => message !== null);
}

function validatorMessage(entry: unknown): string | null {
    if (typeof entry === "string") return entry.trim() || null;
    if (entry && typeof entry === "object") {
        const record = entry as Record<string, unknown>;
        for (const key of ["Message", "message", "ValidationMessage", "Description"]) {
            const value = record[key];
            if (typeof value === "string" && value.trim()) return value;
        }
    }
    return null;
}

const BLOCKING_VALIDATOR_TYPE = 0;

const CONTENT_CONTROL_DIFFERENCES_STATUS = "ContentControlDifferences";
const MODIFIED_ACTION = "Modified";
const ATTRIBUTE_CONTROL_TYPE = "Attribute";
const CLAUSE_CONTROL_TYPE = "Clause";
const ASSOCIATIONS_CONTROL_TYPE = "Associations";
const SIGNATORY_CONTROL_TYPE = "Signatory";
const ACTION_VALIDATOR_SUMMARY_PATH = "Agreement/ShowActionValidatorsSummary";

const APPROVAL_STATUS_PREFIX = "Waiting For Approval";
const VERSION_LABEL_MANDATE_STATUS = "MandatoryFieldsValidation";
const VERSION_LABEL_MANDATE_MESSAGE_FRAGMENT = "version label";

// The version-label-mandate rejection ("Select a version label."), matched the way
// the platform's own add-in matches it before proceeding to resume.
function isVersionLabelMandate(validator: ActionValidator): boolean {
    return (
        validator.ValidationStatus === VERSION_LABEL_MANDATE_STATUS &&
        (validator.ValidationMessage ?? "").includes(VERSION_LABEL_MANDATE_MESSAGE_FRAGMENT)
    );
}

// SaveWithDocument answers 200/201 with a List<ActionValidator>. Entries with
// ActionValidatorType 0 and an explicit ValidateSuccessful=false are rejections —
// no version was created and the failure must be surfaced instead of reported as
// success. Type-1 entries are informational change-summary rows and never block.
export function collectSaveRejections(
    body: string,
    opts: { tolerateVersionLabelMandate?: boolean } = {}
): string[] {
    if (!body.trim()) return [];
    const parsed = parseIciJson(body);
    if (!Array.isArray(parsed)) return [];
    return ActionValidatorsSchema.parse(parsed)
        .filter(
            (validator) =>
                validator.ActionValidatorType === BLOCKING_VALIDATOR_TYPE && validator.ValidateSuccessful === false
        )
        // The last approver has no label picker; the server's own WOPI last-approver
        // carve-out (AgreementModel.cs:5801-5804) normally suppresses this rejection,
        // and the platform client tolerates it as insurance — we do the same.
        .filter((validator) => !(opts.tolerateVersionLabelMandate && isVersionLabelMandate(validator)))
        .map((validator) => validator.ValidationMessage || validator.ValidationStatus || "unspecified validation failure");
}

// True when the body carries any ActionValidator entries — ICI's signal that the
// call was a validation pass rather than a commit.
export function hasSaveValidators(body: string): boolean {
    if (!body.trim()) return false;
    const parsed = parseIciJson(body);
    return Array.isArray(parsed) && parsed.length > 0;
}

export interface ChangeSummaryRow {
    action: string;
    displayName: string;
    tagCount: number | null;
}

export interface ChangeSummary {
    attributes: ChangeSummaryRow[];
    clauses: ChangeSummaryRow[];
    associations: ChangeSummaryRow[];
    signatories: ChangeSummaryRow[];
    rawValidators: JsonValue;
}

function parseContentControlDifferences(message: string): ContentControlDifference[] {
    const differences = ContentControlDifferencesSchema.safeParse(tryParseJson(message));
    return differences.success ? differences.data : [];
}

export function collectChangeSummary(body: string): ChangeSummary | null {
    if (!body.trim()) return null;
    const parsed = parseIciJson(body);
    if (!Array.isArray(parsed)) return null;
    const summary: ChangeSummary = {
        attributes: [],
        clauses: [],
        associations: [],
        signatories: [],
        rawValidators: parsed,
    };
    const groupsByControlType: Record<string, ChangeSummaryRow[]> = {
        [ATTRIBUTE_CONTROL_TYPE]: summary.attributes,
        [CLAUSE_CONTROL_TYPE]: summary.clauses,
        [ASSOCIATIONS_CONTROL_TYPE]: summary.associations,
        [SIGNATORY_CONTROL_TYPE]: summary.signatories,
    };
    for (const validator of ActionValidatorsSchema.parse(parsed)) {
        if (validator.ValidationStatus !== CONTENT_CONTROL_DIFFERENCES_STATUS) continue;
        const message = validator.ValidationMessage;
        if (!message) continue;
        for (const difference of parseContentControlDifferences(message)) {
            if (difference.Action === MODIFIED_ACTION) continue;
            groupsByControlType[difference.DocumentControlType]?.push({
                action: difference.Action,
                displayName: difference.DisplayName,
                tagCount: difference.TagCount ?? null,
            });
        }
    }
    const groups = [summary.attributes, summary.clauses, summary.associations, summary.signatories];
    return groups.some((group) => group.length > 0) ? summary : null;
}

// RecallDocument answers 200 with a truthy body on success; the platform treats a
// falsy/empty body as a failed discard, so mirror that check. A non-JSON body is
// truthy text and counts as success.
export function isRecallAccepted(body: string): boolean {
    const trimmed = body.trim();
    if (!trimmed) return false;
    try {
        return Boolean(JSON.parse(trimmed));
    } catch {
        return true;
    }
}

function assertSaveAccepted(body: string, opts: { tolerateVersionLabelMandate?: boolean }): void {
    const rejections = collectSaveRejections(body, opts);
    if (rejections.length > 0) {
        throw new Error(`[wopi] saveWithDocument rejected: ${rejections.join("; ")}`);
    }
}

function parseValidatorCacheGuid(body: string): string {
    const trimmed = body.trim();
    if (!trimmed) throw new Error("[wopi] setActionValidatorsToCache returned an empty body");
    const parsed = tryParseJson(trimmed);
    if (parsed === undefined) return trimmed;
    if (typeof parsed !== "string") {
        throw new Error(`[wopi] setActionValidatorsToCache returned a non-string cache id: ${trimmed}`);
    }
    return parsed;
}

function buildActionValidatorSummaryUrl(
    iciUrl: string,
    actionValidatorsUniqueId: string,
    entityName: string,
    agreementId: string,
    version: number
): string {
    const query = new URLSearchParams({
        actionValidatorsUniqueId,
        entityName,
        agreementId,
        version: String(version),
        wordAddinRequest: "true",
    });
    return `${iciUrl.replace(/\/+$/, "")}/${ACTION_VALIDATOR_SUMMARY_PATH}?${query.toString()}`;
}

const OWNER_ROLE_NAMES = ["PrimaryOwner", "SecondaryOwner", "ContractManager"];
const APPROVER_ROLE_NAMES = ["Approver", "DeviationApprover"];

// The ICI action names the WOPI surface may render. `Recall` is configured but not
// whitelisted (see WOPI_ACTION_WHITELIST) so it never reaches the action bar.
export type WopiActionValue = "Approve" | "Reject" | "DoneEditDocument" | "RecallDocument" | "Recall";

interface WopiActionConfig {
    value: WopiActionValue;
    showReasonCode: boolean;
    isReasonCodeMandatory: boolean;
}

// Mirrors the platform's Wopi workflow-state action configuration: reason codes are
// required for Reject, optional for Recall, absent otherwise.
const WOPI_ACTION_CONFIG: readonly WopiActionConfig[] = [
    { value: "Approve", showReasonCode: false, isReasonCodeMandatory: false },
    { value: "Reject", showReasonCode: true, isReasonCodeMandatory: true },
    { value: "Recall", showReasonCode: true, isReasonCodeMandatory: false },
    { value: "DoneEditDocument", showReasonCode: false, isReasonCodeMandatory: false },
    { value: "RecallDocument", showReasonCode: false, isReasonCodeMandatory: false },
];

const WOPI_ACTION_WHITELIST: readonly WopiActionValue[] = ["DoneEditDocument", "Approve", "Reject", "RecallDocument"];
const APPROVE_REJECT_ACTIONS: readonly WopiActionValue[] = ["Approve", "Reject"];
const SAVE_DISCARD_ACTIONS: readonly WopiActionValue[] = ["DoneEditDocument", "RecallDocument"];

export interface WopiAction {
    value: WopiActionValue;
    showReasonCode: boolean;
    isReasonCodeMandatory: boolean;
    isNoteAvailable: boolean;
    isNoteMandatory: boolean;
    actionDisplayString: string | null;
    payload: JsonValue | null;
}

export interface WopiRole {
    isApprover: boolean;
    isExternalUser: boolean;
    isOwner: boolean;
}

export interface VersionLabelOption {
    sysId: string;
    displayName: string;
}

// The EnableDocumentVersionLabels tenant config gates the Save New Version label
// dropdown; the save submits the chosen label's SysId as documentVersionLabel.
export interface VersionLabelConfig {
    isMandatory: boolean;
    labels: VersionLabelOption[];
}

export interface WopiUiVisibility {
    teamPanel: boolean;
    approveReject: boolean;
    saveDiscard: boolean;
}

export interface WopiSessionState {
    isWopi: boolean;
    environment: BackendEnvironment | null;
    iciApiUrl: string | null;
    iciUrl: string | null;
    instanceId: string | null;
    entityName: string | null;
    contractType: string | null;
    isAssociatedDocument: boolean;
    uiVisibility: WopiUiVisibility;
    status: string | null;
    agreementVersion: number | null;
    role: WopiRole;
    panelDetails: WopiPanelDetails | null;
}

export interface SaveWithDocumentOptions {
    note?: string;
    // The selected version label's SysId (documentVersionLabel on the PUT).
    versionLabel?: string;
    answer?: boolean;
    tolerateVersionLabelMandate?: boolean;
}

export type WopiSessionListener = (state: WopiSessionState) => void;

const DEFAULT_UI_VISIBILITY: WopiUiVisibility = { teamPanel: true, approveReject: true, saveDiscard: true };
const DEFAULT_ROLE: WopiRole = { isApprover: false, isExternalUser: false, isOwner: false };

function nonWopiState(): WopiSessionState {
    return {
        isWopi: false,
        environment: null,
        iciApiUrl: null,
        iciUrl: null,
        instanceId: null,
        entityName: null,
        contractType: null,
        isAssociatedDocument: false,
        uiVisibility: { ...DEFAULT_UI_VISIBILITY },
        status: null,
        agreementVersion: null,
        role: { ...DEFAULT_ROLE },
        panelDetails: null,
    };
}

function isTruthyFlag(value: string | null): boolean {
    return value != null && ["true", "1", "yes"].includes(value.trim().toLowerCase());
}

// The platform sometimes hands ICI_API_URL with a trailing `/api`; drop it so the
// backend can append each action's own `/api/...` path.
function normalizeIciApiUrl(raw: string | null): string | null {
    if (!raw) return null;
    return raw.replace(/\/api\/?$/i, "");
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

// Params may carry a base64 document or a large payload under `body`; summarize it so
// the trace stays readable and never dumps document bytes into the console.
function redactParams(params: Record<string, JsonValue>): Record<string, unknown> {
    const summary: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params)) {
        if (key === "body" && typeof value === "string") {
            summary[key] = `<string len=${value.length}>`;
        } else if (key === "body") {
            summary[key] = "<object>";
        } else {
            summary[key] = value;
        }
    }
    return summary;
}

// Chrome truncates long console strings; log the parsed body as an expandable
// object instead, falling back to the raw text when it isn't JSON.
function loggableBody(body: string): unknown {
    const trimmed = body.trim();
    if (!trimmed) return "<empty>";
    try {
        return JSON.parse(trimmed);
    } catch {
        return trimmed;
    }
}

// Names the resolved postMessage target frame for tracing, without dereferencing a
// cross-origin window (which would throw).
function describeMessageTarget(target: Window | null): string {
    if (target === null) return "null";
    try {
        if (target === window.top) return "window.top";
        if (target === window.parent.parent) return "window.parent.parent";
        if (window.parent.parent && target === window.parent.parent.parent) return "window.parent.parent.parent";
    } catch {
        return "cross-origin";
    }
    return "other";
}

function parseIciJson(body: string): JsonValue {
    return (body.trim() ? JSON.parse(body) : null) as JsonValue;
}

function tryParseJson(raw: string): JsonValue | undefined {
    try {
        return JSON.parse(raw) as JsonValue;
    } catch {
        return undefined;
    }
}

// GetAgreement double-encodes: the body is a JSON string whose contents are the
// serialized agreement, so parse a second time when the first parse yields a string.
function parseAgreementBody(body: string): JsonValue {
    if (!body.trim()) return {};
    const once = parseIciJson(body);
    return typeof once === "string" ? (JSON.parse(once) as JsonValue) : once;
}

export function selectSessionParticipant(
    candidates: AgreementIdentity[],
    panels: (WopiPanelDetails | null)[],
    currentUserEmail: string
): AgreementIdentity | null {
    const email = currentUserEmail.toLowerCase();
    const participating = candidates.filter((_, index) =>
        (panels[index]?.ParticipatedUsers ?? []).some(
            (participant) => (participant.Email ?? "").toLowerCase() === email
        )
    );
    return participating.length === 1 ? participating[0] : null;
}

// A Configurations value is itself JSON, sometimes authored with single quotes
// (invalid JSON) — normalize them before parsing, as the platform client does.
function parseNestedIciJson(raw: string): JsonValue {
    try {
        return JSON.parse(raw) as JsonValue;
    } catch {
        return JSON.parse(raw.replaceAll("'", "\"")) as JsonValue;
    }
}

// `"true|false|true"` → flags; anything malformed or absent → the platform default of
// all-visible (`"true|true|true"`).
export function parseAddinUiVisibility(raw: string | null): WopiUiVisibility {
    if (!raw) return { ...DEFAULT_UI_VISIBILITY };
    const parts = raw.split("|").map((part) => part.trim().toLowerCase());
    if (parts.length !== 3) return { ...DEFAULT_UI_VISIBILITY };
    return {
        teamPanel: parts[0] === "true",
        approveReject: parts[1] === "true",
        saveDiscard: parts[2] === "true",
    };
}

// A user who is also an owner/contract-manager is never treated as an approver.
export function computeRole(teamMembers: TeamMember[], user: CurrentUser): WopiRole {
    const isExternalUser = user.TypeOfUser === "External";
    const identifier = user.Identifier ?? null;
    if (identifier == null) {
        wlog("computeRole: current user has no identifier → no team roles", { isExternalUser });
        return { isApprover: false, isExternalUser, isOwner: false };
    }
    const userMembers = teamMembers.filter((member) => member.UserIdentifier === identifier);
    const isOwner = userMembers.some((member) => member.RoleName != null && OWNER_ROLE_NAMES.includes(member.RoleName));
    const hasApproverRole = userMembers.some(
        (member) => member.RoleName != null && APPROVER_ROLE_NAMES.includes(member.RoleName)
    );
    wlog("computeRole", {
        identifier,
        memberRoles: userMembers.map((member) => member.RoleName),
        isOwner,
        hasApproverRole,
        isExternalUser,
    });
    return { isApprover: hasApproverRole && !isOwner, isExternalUser, isOwner };
}

// The reason an ICI-offered action stays off the action bar, or null when it shows.
function actionDropReason(action: WopiAction, uiVisibility: WopiUiVisibility): string | null {
    if (!WOPI_ACTION_WHITELIST.includes(action.value)) return "not whitelisted for WOPI";
    if (!uiVisibility.approveReject && APPROVE_REJECT_ACTIONS.includes(action.value)) {
        return "approveReject visibility flag off";
    }
    if (!uiVisibility.saveDiscard && SAVE_DISCARD_ACTIONS.includes(action.value)) {
        return "saveDiscard visibility flag off";
    }
    return null;
}

export function filterVisibleActions(actions: WopiAction[], uiVisibility: WopiUiVisibility): WopiAction[] {
    return actions.filter((action) => actionDropReason(action, uiVisibility) == null);
}

function normalizeVisibleActions(raw: IciVisibleAction[]): WopiAction[] {
    const actions: WopiAction[] = [];
    for (const item of raw) {
        const key = item.WorkflowAction?.DisplayString ?? item.ActionName;
        const config = WOPI_ACTION_CONFIG.find((candidate) => candidate.value === key);
        if (!config) continue;
        actions.push({
            value: config.value,
            showReasonCode: config.showReasonCode,
            isReasonCodeMandatory: config.isReasonCodeMandatory,
            isNoteAvailable: item.RoleActionMap?.IsNoteAvailable ?? false,
            isNoteMandatory: item.RoleActionMap?.IsNoteMandatory ?? false,
            actionDisplayString: item.RoleActionMap?.ActionDisplayString ?? null,
            payload: item.WorkflowAction?.WFAction ?? null,
        });
    }
    return actions;
}

export function nextPollInterval(current: number): number {
    return Math.min(current * 2, POLL_MAX_INTERVAL_MS);
}

export function deepEqual<T>(a: T, b: T): boolean {
    if (a === b) return true;
    if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    const aRecord = a as Record<string, unknown>;
    const bRecord = b as Record<string, unknown>;
    const aKeys = Object.keys(aRecord);
    const bKeys = Object.keys(bRecord);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => deepEqual(aRecord[key], bRecord[key]));
}

type PostMessageEventType = "SendToken" | "ReloadDocument" | "ViewAllChanges";

export type BeginSaveNewVersionResult =
    | { committed: true }
    | { committed: false; changeSummary: ChangeSummary | null };

/**
 * The single home for all WOPI behavior: context resolution, the postMessage
 * handshakes, the WOPI token, every proxied ICI call, role/action computation,
 * ADDIN_UI_VISIBILITY parsing, and the panel-details poller. React reads the
 * subscribable state snapshot and calls these methods.
 */
export class WopiSession {
    private state: WopiSessionState = nonWopiState();
    private token: WopiToken | null = null;
    private currentUser: CurrentUser | null = null;
    private rawVisibleActions: WopiAction[] = [];
    private versionLabelConfig: VersionLabelConfig | null = null;
    private readonly listeners = new Set<WopiSessionListener>();
    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    private pollGeneration = 0;
    private endingSessionLocally = false;
    private pendingSaveNewVersion: { params: Record<string, JsonValue>; opts: SaveWithDocumentOptions } | null = null;

    getState(): WopiSessionState {
        return this.state;
    }

    subscribe(listener: WopiSessionListener): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    private setState(patch: Partial<WopiSessionState>): void {
        this.state = { ...this.state, ...patch };
        this.listeners.forEach((listener) => listener(this.state));
    }

    // WOPI_MODE + ICI_URL/ICI_API_URL + ADDIN_UI_VISIBILITY custom properties + the
    // agreement identity from the document's ICMAgreementNamespace custom XML part;
    // matches the returned app URL against the env registry.
    async resolve(): Promise<void> {
        try {
            wlog("resolve: reading WOPI custom properties");
            const props = await fetchAllCustomProperties();
            wlog("resolve: all custom document properties", props);

            if (!isTruthyFlag(props[WOPI_PROPS.wopiMode])) {
                wlog(`resolve: WOPI_MODE=${props[WOPI_PROPS.wopiMode] ?? "<unset>"} → non-WOPI session`);
                this.setState(nonWopiState());
                return;
            }

            const uiVisibility = parseAddinUiVisibility(props[WOPI_PROPS.addinUiVisibility]);
            const iciApiUrl = normalizeIciApiUrl(props[WOPI_PROPS.iciApiUrl]);
            const iciUrl = props[WOPI_PROPS.iciUrl] ?? null;

            let environment: BackendEnvironment | null = null;
            try {
                const url = await this.requestUrl();
                environment = findEnvironmentByAppUrl(url);
                wlog(`resolve: ReadUrl=${url} → environment ${environment ? "matched" : "NOT matched"}`);
            } catch (error) {
                wlog("resolve: requestUrl failed (env will be unmatched)", error);
                reportError(error, { level: "warning", extra: { component: "WopiSession", action: "resolve_request_url" } });
            }

            this.setState({ isWopi: true, environment, iciApiUrl, iciUrl, uiVisibility });

            const identity = await this.resolveAgreementIdentity();

            wlog("resolve: WOPI session active", {
                iciApiUrl,
                iciUrl,
                instanceId: identity?.instanceId ?? null,
                contractType: identity?.entityName ?? null,
                isAssociatedDocument: identity?.isAssociatedDocument ?? false,
                uiVisibility,
                hasEnvironment: environment != null,
            });
            this.setState({
                instanceId: identity?.instanceId ?? null,
                entityName: identity?.entityName ?? null,
                contractType: identity?.entityName ?? null,
                isAssociatedDocument: identity?.isAssociatedDocument ?? false,
            });
        } catch (error) {
            wlog("resolve: failed → non-WOPI session", error);
            reportError(error, { level: "warning", extra: { component: "WopiSession", action: "resolve_session" } });
            this.setState(nonWopiState());
        }
    }

    // A failed identity extraction leaves an active WOPI session with an
    // unresolved instance — never demotes the session to non-WOPI.
    private async resolveAgreementIdentity(): Promise<AgreementIdentity | null> {
        try {
            const partXml = await readAgreementIdentityPartXml();
            if (partXml == null) return null;
            const candidates = parseAgreementIdentityCandidates(partXml);
            if (candidates.length === 1) {
                wlog("resolve: identity", candidates[0]);
                return candidates[0];
            }
            const participant = await this.probeSessionParticipation(candidates);
            wlog("resolve: multiple identities in document", {
                candidates: candidates.map((candidate) => `${candidate.entityName}/${candidate.instanceId}`),
                selected: (participant ?? candidates[0]).instanceId,
                matchedByWopiSessionParticipation: participant != null,
            });
            return participant ?? candidates[0];
        } catch (error) {
            wlog("resolve: agreement identity extraction failed (instance will be unresolved)", error);
            reportError(error, { level: "warning", extra: { component: "WopiSession", action: "resolve_agreement_identity" } });
            return null;
        }
    }

    private async probeSessionParticipation(candidates: AgreementIdentity[]): Promise<AgreementIdentity | null> {
        try {
            const email = (await this.getCurrentUser()).EmailID ?? "";
            if (!email) return null;
            const panels = await Promise.all(
                candidates.map(async (candidate) => {
                    try {
                        const response = await this.callIci("getWopiPanelDetails", {
                            instanceId: candidate.instanceId,
                            entityName: candidate.entityName,
                        });
                        if (!response.body.trim()) return null;
                        return WopiPanelDetailsSchema.parse(parseIciJson(response.body));
                    } catch {
                        return null;
                    }
                })
            );
            return selectSessionParticipant(candidates, panels, email);
        } catch (error) {
            wlog("resolve: session participation probe failed", error);
            return null;
        }
    }

    // SendUrl → ReadUrl (the ICI page is exactly two frames up).
    async requestUrl(): Promise<string> {
        return this.postMessageHandshake(WOPI_MESSAGE.request, ReadUrlMessageSchema, (parsed) => parsed.data.url);
    }

    // SendToken → ReadToken; caches the token for subsequent ICI calls.
    async requestToken(): Promise<WopiToken> {
        const token = await this.postMessageHandshake(
            WOPI_MESSAGE.tokenRequest,
            ReadTokenMessageSchema,
            (parsed) => parsed.data
        );
        this.token = token;
        wlog(`requestToken: token cached (len=${token.token.length}, ttl=${token.accessTokenTtl ?? "n/a"})`);
        return token;
    }

    // The ICI page replies with targetOrigin "*", so we cannot pin its origin; instead
    // we accept only messages from the exact frame we addressed (window.parent.parent).
    private postMessageHandshake<S extends z.ZodTypeAny, R>(
        requestType: string,
        schema: S,
        extract: (parsed: z.infer<S>) => R
    ): Promise<R> {
        return new Promise<R>((resolve, reject) => {
            const iciFrame = window.parent.parent;
            wlog(`postMessage → ${requestType} to window.parent.parent`);
            const timer = setTimeout(() => {
                window.removeEventListener("message", onMessage);
                wlog(`postMessage ✗ ${requestType} timed out after ${POST_MESSAGE_TIMEOUT_MS}ms`);
                reject(new Error(`WOPI ${requestType} timed out`));
            }, POST_MESSAGE_TIMEOUT_MS);

            function onMessage(event: MessageEvent): void {
                if (event.source !== iciFrame) return;
                const parsed = schema.safeParse(event.data);
                if (!parsed.success) return;
                clearTimeout(timer);
                window.removeEventListener("message", onMessage);
                wlog(`postMessage ← ${requestType} reply accepted`);
                resolve(extract(parsed.data));
            }

            window.addEventListener("message", onMessage);
            iciFrame.postMessage({ type: requestType }, "*");
        });
    }

    private async ensureToken(): Promise<WopiToken> {
        return this.token ?? this.requestToken();
    }

    // Every ICI call funnels through here: POST the allowlisted action to the backend
    // proxy with the Dioptra bearer key + ICI token; the proxy makes the call
    // server-to-server (the browser has no CORS grant for the ICI origin).
    private async callIci(action: string, params: Record<string, JsonValue>): Promise<IciProxyResponse> {
        const apiKey = getApiKey();
        if (!apiKey) throw new Error("[wopi] no Dioptra API key — sign in before calling ICI");
        if (!this.state.iciApiUrl) throw new Error("[wopi] ICI_API_URL not resolved");
        const token = await this.ensureToken();

        wlog(`callIci → ${action}`, redactParams(params));
        const response = await safeFetch(
            `${getEnv("API_BASE_URL")}/wopi/ici-proxy`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${apiKey}`,
                },
                body: JSON.stringify({
                    ici_api_url: this.state.iciApiUrl,
                    token: token.token,
                    request_verification_token: token.requestVerificationToken,
                    action,
                    params,
                }),
            },
            proxyRetryConfig(action),
        );

        // The proxy always answers 200 and carries the ICI outcome in the envelope, so
        // an ICI-side failure (ok=false) must be raised here or callers would treat a
        // failed save/recall/resume as success.
        const parsed = IciProxyResponseSchema.parse(await response.json());
        wlog(`callIci ← ${action} status=${parsed.status} ok=${parsed.ok}`, loggableBody(parsed.body));
        if (!parsed.ok) {
            wlog(`callIci ✗ ${action} raising IciProxyError status=${parsed.status}`);
            throw new IciProxyError(action, parsed.status, parsed.body);
        }
        return parsed;
    }

    private requireInstance(): { instanceId: string; entityName: string } {
        const { instanceId, entityName } = this.state;
        if (!instanceId || !entityName) throw new Error("[wopi] agreement instance not resolved");
        return { instanceId, entityName };
    }

    private async getCurrentUser(): Promise<CurrentUser> {
        if (this.currentUser) return this.currentUser;
        const response = await this.callIci("getCurrentUser", {});
        this.currentUser = CurrentUserSchema.parse(parseIciJson(response.body));
        return this.currentUser;
    }

    // The session user's email, available once loadSessionState has run. Used to
    // exclude the acting user from the active-participants save warning.
    getCurrentUserEmail(): string | null {
        return this.currentUser?.EmailID ?? null;
    }

    // getAgreement + getAssociationInstances (+ resolveUserGroups) + GetCurrentUser →
    // role/status; getAvailableVisibleActions → the raw action list.
    async loadSessionState(): Promise<void> {
        const { instanceId, entityName } = this.requireInstance();
        wlog(`loadSessionState: fetching agreement/team/actions for ${entityName}/${instanceId}`);
        const [user, agreement, members, rawActions] = await Promise.all([
            this.getCurrentUser(),
            this.fetchAgreementStatus(instanceId, entityName),
            this.loadTeamMembers(instanceId, entityName),
            this.fetchVisibleActions(instanceId, entityName),
        ]);
        this.rawVisibleActions = rawActions;
        const role = computeRole(members, user);
        const visibleActions = filterVisibleActions(rawActions, this.state.uiVisibility);
        const droppedActions = rawActions
            .map((action) => ({
                action: action.value,
                reason: actionDropReason(action, this.state.uiVisibility),
            }))
            .filter((entry) => entry.reason != null);
        wlog("loadSessionState: resolved", {
            status: agreement.status,
            agreementVersion: agreement.version,
            role,
            currentUser: { identifier: user.Identifier ?? null, typeOfUser: user.TypeOfUser ?? null },
            teamMembers: members.length,
            uiVisibility: this.state.uiVisibility,
            rawActions: rawActions.map((action) => action.value),
            visibleActions: visibleActions.map((action) => action.value),
            droppedActions,
        });
        this.setState({ status: agreement.status, agreementVersion: agreement.version, role });
    }

    private async fetchAgreementStatus(
        instanceId: string,
        entityName: string
    ): Promise<{ status: string | null; version: number | null }> {
        const response = await this.callIci("getAgreement", {
            agreementId: instanceId,
            contractType: entityName,
            includeChildCollections: false,
            fetchAuditEntries: false,
        });
        const agreement = AgreementSchema.parse(parseAgreementBody(response.body));
        const status = agreement.Status ?? null;
        const lastStatus = agreement.LastStatus ?? null;
        if (status !== lastStatus) {
            wlog("fetchAgreementStatus: Status and LastStatus differ", { status, lastStatus });
        }
        return { status: lastStatus, version: agreement.Version ?? null };
    }

    // Team members come from GetAssociationInstances; groups are expanded via
    // ResolveUserGroups so the role check sees individual users.
    private async loadTeamMembers(instanceId: string, entityName: string): Promise<TeamMember[]> {
        const response = await this.callIci("getAssociationInstances", {
            parentName: entityName,
            parentInstanceId: instanceId,
            associatedEntityName: "TeamMember",
            associationName: "AgreementTeamMember",
        });
        if (!response.body.trim()) return [];
        const raw = parseIciJson(response.body);
        const members = TeamMembersSchema.parse(raw);
        if (!members.some((member) => member.IsGroup)) return members;
        const resolved = await this.callIci("resolveUserGroups", { body: raw });
        if (!resolved.body.trim()) return members;
        return TeamMembersSchema.parse(parseIciJson(resolved.body));
    }

    private async fetchVisibleActions(instanceId: string, entityName: string): Promise<WopiAction[]> {
        const response = await this.callIci("getAvailableVisibleActions", { instanceId, entityName });
        if (!response.body.trim()) return [];
        const raw = IciVisibleActionsSchema.parse(parseIciJson(response.body));
        wlog(
            "fetchVisibleActions: raw ICI actions",
            raw.map((item) => ({
                actionName: item.ActionName ?? null,
                displayString: item.WorkflowAction?.DisplayString ?? null,
            }))
        );
        return normalizeVisibleActions(raw);
    }

    getVisibleActions(): WopiAction[] {
        return filterVisibleActions(this.rawVisibleActions, this.state.uiVisibility);
    }

    // True once this user has initiated the save or discard that ends the session,
    // so the presence poller suppresses the session-ending/discarded dialogs for the
    // acting user (other participants still see them).
    isEndingSessionLocally(): boolean {
        return this.endingSessionLocally;
    }

    private async buildSaveWithDocumentParams(opts: SaveWithDocumentOptions): Promise<Record<string, JsonValue>> {
        const { instanceId, entityName } = this.requireInstance();
        const user = await this.getCurrentUser();
        if (user.Identifier == null) throw new Error("[wopi] current user has no identifier");

        const base64 = await getDocumentFileToBase64();
        wlog(`saveWithDocument: read document (base64 len=${base64.length})`);
        return {
            agreementId: instanceId,
            fileExtension: SAVE_FILE_EXTENSION,
            loggedInUserId: user.Identifier,
            entityName,
            addAuditLogForUpload: false,
            wordAddinRequest: true,
            instanceNotes: opts.note ?? "",
            actualFileName: "",
            userDefinedVersion: 0,
            documentVersionLabel: opts.versionLabel ?? "",
            body: base64,
        };
    }

    private async submitSaveWithDocument(params: Record<string, JsonValue>, opts: SaveWithDocumentOptions): Promise<string> {
        const response = await this.callIci("saveWithDocument", params);
        assertSaveAccepted(response.body, opts);
        return response.body;
    }

    async saveWithDocument(opts: SaveWithDocumentOptions = {}): Promise<void> {
        wlog("saveWithDocument: start", { note: opts.note ?? "", versionLabel: opts.versionLabel ?? "", answer: opts.answer ?? false });
        const params = await this.buildSaveWithDocumentParams(opts);
        if (opts.answer) params.answer = true;

        // Mark the session ending only around the terminal mutation and clear it if the
        // save fails, so a failed save never permanently mutes this user's
        // session-ending/discarded dialogs.
        this.endingSessionLocally = true;
        try {
            await this.updateWopiSessionState(true);
            const body = await this.submitSaveWithDocument(params, opts);

            // Without answer=True, a response carrying validator entries — even
            // purely informational ones — is a validation pass only: ICI committed
            // nothing and waits for the save to be re-submitted with answer=True.
            if (!params.answer && hasSaveValidators(body)) {
                wlog("saveWithDocument: validation pass returned entries → confirming with answer=True");
                params.answer = true;
                await this.submitSaveWithDocument(params, opts);
            }
            wlog("saveWithDocument: committed");
        } catch (error) {
            wlog("saveWithDocument: failed → aborting session ending", error);
            await this.abortSessionEnding();
            throw error;
        }
    }

    async beginSaveNewVersion(opts: SaveWithDocumentOptions = {}): Promise<BeginSaveNewVersionResult> {
        wlog("beginSaveNewVersion: start", {
            note: opts.note ?? "",
            versionLabel: opts.versionLabel ?? "",
            status: this.state.status,
        });
        this.probeLastApproverForSaveFlow();
        const params = await this.buildSaveWithDocumentParams(opts);

        this.endingSessionLocally = true;
        try {
            await this.updateWopiSessionState(true);
            const body = await this.submitSaveWithDocument(params, { ...opts, tolerateVersionLabelMandate: true });
            if (!hasSaveValidators(body)) {
                wlog("beginSaveNewVersion: committed on the validation pass");
                return { committed: true };
            }
            const changeSummary = collectChangeSummary(body);
            this.pendingSaveNewVersion = { params, opts };
            wlog("beginSaveNewVersion: validation pass returned entries → awaiting confirmation", {
                changeSummary,
            });
            return { committed: false, changeSummary };
        } catch (error) {
            wlog("beginSaveNewVersion: failed → aborting session ending", error);
            await this.abortSessionEnding();
            throw error;
        }
    }

    private probeLastApproverForSaveFlow(): void {
        if (!(this.state.status ?? "").startsWith(APPROVAL_STATUS_PREFIX)) return;
        this.isLastApprover("Approve").catch((error) => {
            wlog("beginSaveNewVersion: last-approver probe failed", error);
        });
    }

    async confirmSaveNewVersion(opts: Pick<SaveWithDocumentOptions, "note" | "versionLabel"> = {}): Promise<void> {
        const pending = this.pendingSaveNewVersion;
        if (!pending) throw new Error("[wopi] no pending Save New Version to confirm");
        this.pendingSaveNewVersion = null;
        const params: Record<string, JsonValue> = {
            ...pending.params,
            instanceNotes: opts.note ?? pending.params.instanceNotes,
            documentVersionLabel: opts.versionLabel ?? pending.params.documentVersionLabel,
            answer: true,
        };
        try {
            await this.submitSaveWithDocument(params, pending.opts);
            wlog("confirmSaveNewVersion: committed");
        } catch (error) {
            wlog("confirmSaveNewVersion: failed → aborting session ending", error);
            await this.abortSessionEnding();
            throw error;
        }
    }

    async cancelSaveNewVersion(): Promise<void> {
        wlog("cancelSaveNewVersion");
        this.pendingSaveNewVersion = null;
        await this.abortSessionEnding();
    }

    async viewAllChanges(rawValidators: JsonValue): Promise<void> {
        const { instanceId, entityName } = this.requireInstance();
        const { iciUrl, agreementVersion } = this.state;
        if (!iciUrl) throw new Error("[wopi] ICI_URL not resolved");
        if (agreementVersion == null) throw new Error("[wopi] agreement version not resolved");
        const cacheVersion = Math.max(agreementVersion, 1);

        const response = await this.callIci("setActionValidatorsToCache", {
            entityName,
            agreementId: instanceId,
            version: cacheVersion,
            body: rawValidators,
        });
        const url = buildActionValidatorSummaryUrl(
            iciUrl,
            parseValidatorCacheGuid(response.body),
            entityName,
            instanceId,
            cacheVersion
        );

        const embeddedTarget = this.resolveSapEmbeddedTarget();
        if (embeddedTarget) {
            wlog(`viewAllChanges: posting to ${describeMessageTarget(embeddedTarget)}`, { url });
            embeddedTarget.postMessage(
                {
                    type: "ViewAllChanges",
                    url,
                    isExternalUser: this.state.role.isExternalUser,
                    contractTypeName: entityName,
                    agreementVersion: cacheVersion,
                    icmAgreementId: instanceId,
                },
                "*"
            );
            return;
        }
        wlog("viewAllChanges: opening in a new tab", { url });
        window.open(url, "_blank");
    }

    private resolveSapEmbeddedTarget(): Window | null {
        try {
            if (!window.parent || !window.parent.parent) return null;
            if (window.parent.parent === window.top) return null;
            return this.resolveMessageTarget("ViewAllChanges");
        } catch {
            return null;
        }
    }

    private async updateWopiSessionState(value: boolean): Promise<void> {
        const { instanceId, entityName } = this.requireInstance();
        wlog(`updateWopiSessionState: ${value}`);
        await this.callIci("updateWopiSessionState", { instanceId, entityName, value });
    }

    // A rejected save must not leave the other participants seeing "session ending";
    // best-effort revert of the flag set before the PUT.
    private async revertWopiSessionEnding(): Promise<void> {
        try {
            await this.updateWopiSessionState(false);
        } catch (error) {
            wlog("saveWithDocument: failed to revert the session-ending state", error);
            reportError(error, { level: "warning", extra: { component: "WopiSession", action: "revert_session_ending" } });
        }
    }

    // A failure after the session was marked ending (a rejected save, or a failed
    // resume after the last approver's auto-save) must not leave other participants
    // seeing "session ending"; clears the local flag and best-effort reverts ICI's.
    async abortSessionEnding(): Promise<void> {
        this.endingSessionLocally = false;
        await this.revertWopiSessionEnding();
    }

    async recall(): Promise<void> {
        const { instanceId, entityName } = this.requireInstance();
        wlog("recall: start");
        this.endingSessionLocally = true;
        try {
            const response = await this.callIci("recallDocument", { instanceId, entityName });
            if (!isRecallAccepted(response.body)) {
                throw new Error("[wopi] recallDocument rejected: empty or falsy response body");
            }
            wlog("recall: done");
        } catch (error) {
            wlog("recall: failed → clearing endingSessionLocally", error);
            this.endingSessionLocally = false;
            throw error;
        }
    }

    async isLastApprover(actionName: string): Promise<boolean> {
        const { instanceId, entityName } = this.requireInstance();
        const response = await this.callIci("isLastApprover", {
            entityInstanceId: instanceId,
            entityName,
            actionName,
        });
        const result = IsLastApproverSchema.parse(parseIciJson(response.body)).data;
        wlog(`isLastApprover(${actionName}) → ${result}`);
        return result;
    }

    // ResumeWorkFlowMobile. The last approver's Approve races the platform's own save,
    // so retry a 409 up to three times.
    async resumeWorkflow(action: WopiAction, note: string, lastApprover: boolean, reasonCodeId?: string): Promise<void> {
        const { instanceId, entityName } = this.requireInstance();
        const user = await this.getCurrentUser();
        if (user.Identifier == null) throw new Error("[wopi] current user has no identifier");

        const maxRetries = lastApprover ? RESUME_WORKFLOW_MAX_RETRIES : 0;
        wlog(`resumeWorkflow: ${action.value} (lastApprover=${lastApprover}, maxRetries=${maxRetries}, reasonCodeId=${reasonCodeId ?? "none"})`);

        const params: Record<string, JsonValue> = {
            entityName,
            id: instanceId,
            note,
            loggedinUserid: user.Identifier,
            reasonCodeId: reasonCodeId ?? NULL_GUID,
            resumeSynchronously: true,
            body: action.payload ?? {},
        };

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const response = await this.callIci("resumeWorkflow", params);
                assertResumeAccepted(response.body);
                wlog(`resumeWorkflow: ${action.value} accepted (attempt ${attempt + 1})`);
                return;
            } catch (error) {
                if (error instanceof IciProxyError && error.status === CONFLICT_STATUS && attempt < maxRetries) {
                    wlog(`resumeWorkflow: 409 conflict on attempt ${attempt + 1}, retrying in ${RESUME_RETRY_DELAY_MS}ms`);
                    await delay(RESUME_RETRY_DELAY_MS);
                    continue;
                }
                wlog(`resumeWorkflow: ${action.value} failed on attempt ${attempt + 1}`, error);
                throw error;
            }
        }
    }

    async getReasonCodes(): Promise<ReasonCode[]> {
        const response = await this.callIci("getReasons", {});
        if (!response.body.trim()) return [];
        const codes = ReasonCodesSchema.parse(parseIciJson(response.body));
        wlog(`getReasonCodes → ${codes.length} codes`);
        return codes;
    }

    // Configurations?configKeys=Core.Notes.CharacterLimit, defaulting to 1000.
    async getNotesCharacterLimit(): Promise<number> {
        try {
            const response = await this.callIci("getConfigurations", {
                configKeys: NOTES_CHARACTER_LIMIT_CONFIG_KEY,
            });
            const config = ConfigurationsSchema.parse(parseIciJson(response.body));
            const limit = Number(config[NOTES_CHARACTER_LIMIT_CONFIG_KEY]);
            const resolved = Number.isFinite(limit) && limit > 0 ? limit : DEFAULT_NOTES_CHARACTER_LIMIT;
            wlog(`getNotesCharacterLimit → ${resolved}`);
            return resolved;
        } catch (error) {
            wlog(`getNotesCharacterLimit: failed → default ${DEFAULT_NOTES_CHARACTER_LIMIT}`, error);
            reportError(error, { level: "warning", extra: { component: "WopiSession", action: "get_notes_character_limit" } });
            return DEFAULT_NOTES_CHARACTER_LIMIT;
        }
    }

    // Whether the tenant requires a version label on Save New Version, and the
    // selectable labels (deprecated ones dropped). Cached for the session.
    async getVersionLabelConfig(): Promise<VersionLabelConfig> {
        if (this.versionLabelConfig) return this.versionLabelConfig;
        const { enabled, isMandatory } = await this.fetchVersionLabelsEnablement();
        const labels = enabled ? await this.fetchVersionLabels() : [];
        this.versionLabelConfig = { isMandatory: enabled && isMandatory, labels };
        wlog("getVersionLabelConfig", { enabled, isMandatory: this.versionLabelConfig.isMandatory, labels: labels.length });
        return this.versionLabelConfig;
    }

    private async fetchVersionLabelsEnablement(): Promise<{ enabled: boolean; isMandatory: boolean }> {
        const response = await this.callIci("getConfigurations", { configKeys: VERSION_LABELS_CONFIG_KEY });
        const config = ConfigurationsSchema.parse(parseIciJson(response.body));
        const raw = config[VERSION_LABELS_CONFIG_KEY];
        if (!raw) return { enabled: false, isMandatory: false };
        const parsed = VersionLabelsConfigSchema.parse(parseNestedIciJson(raw));
        return { enabled: parsed.EnableDocumentVersionLabels, isMandatory: parsed.IsMandatory };
    }

    private async fetchVersionLabels(): Promise<VersionLabelOption[]> {
        const response = await this.callIci("getVersionLabels", {});
        if (!response.body.trim()) return [];
        return VersionLabelsSchema.parse(parseIciJson(response.body))
            .filter((label) => !label.IsDeprecated)
            .map((label) => ({ sysId: label.SysId, displayName: label.DisplayName || label.SysId }));
    }

    // GetWopiPanelDetails on a 5s → 60s doubling cadence; identical payloads are
    // skipped so an idle session never re-renders the panel.
    startPolling(onUpdate: (details: WopiPanelDetails) => void): void {
        if (this.pollTimer !== null) {
            wlog("startPolling: already polling, ignoring");
            return;
        }
        const generation = ++this.pollGeneration;
        wlog(`startPolling: generation ${generation}`);
        let interval = POLL_INITIAL_INTERVAL_MS;

        const tick = async (): Promise<void> => {
            try {
                const details = await this.fetchPanelDetails();
                if (details && !deepEqual(details, this.state.panelDetails)) {
                    wlog("poll: panel details changed", {
                        participated: details.ParticipatedUsers.length,
                        actionGroups: details.ActionUsers.length,
                        sessionEnding: details.IsWopiSessionEnding,
                        sessionDiscarded: details.IsWopiSessionDiscarded,
                    });
                    this.setState({ panelDetails: details });
                    onUpdate(details);
                } else {
                    wlog(`poll: no change (next in ${interval}ms)`);
                }
            } catch (error) {
                wlog("poll: tick failed", error);
                reportError(error, { level: "warning", extra: { component: "WopiSession", action: "poll_panel_details" } });
            } finally {
                // A tick that awaited across a stop/restart belongs to a stale
                // generation; only the current loop may reschedule itself, so a
                // restart never leaves two tick loops running in parallel.
                if (this.pollTimer !== null && this.pollGeneration === generation) {
                    this.pollTimer = setTimeout(tick, interval);
                    interval = nextPollInterval(interval);
                }
            }
        };

        this.pollTimer = setTimeout(tick, 0);
    }

    stopPolling(): void {
        this.pollGeneration++;
        if (this.pollTimer !== null) {
            wlog("stopPolling");
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private async fetchPanelDetails(): Promise<WopiPanelDetails | null> {
        const { instanceId, entityName } = this.requireInstance();
        const response = await this.callIci("getWopiPanelDetails", { instanceId, entityName });
        if (!response.body.trim()) return null;
        return WopiPanelDetailsSchema.parse(parseIciJson(response.body));
    }

    postReloadDocument(): void {
        const target = this.resolveMessageTarget("ReloadDocument");
        wlog(`postReloadDocument: target=${describeMessageTarget(target)} isExternalUser=${this.state.role.isExternalUser}`);
        target?.postMessage({ type: "ReloadDocument", isExternalUser: this.state.role.isExternalUser }, "*");
    }

    // window.parent = Word host; window.parent.parent = ICI WOPI editor; its parent =
    // the SAP Nexus page when embedded. ReloadDocument targets the top when the
    // editor is the top, else the SAP page above it.
    private resolveMessageTarget(eventType: PostMessageEventType): Window | null {
        try {
            if (!window.parent || !window.parent.parent) return null;
            if (window.parent.parent === window.top) return window.top;
            if (eventType === "SendToken") return window.parent.parent;
            return window.parent.parent.parent ?? null;
        } catch {
            return null;
        }
    }
}

export const wopiSession = new WopiSession();
